import React, { useMemo, useState } from 'react';
import { Select, Alert, List, Button, Modal, Typography, Space } from 'antd';
import { SCENARIO_THEME } from './scenarioTheme';
import { Trans, useTranslation } from 'react-i18next';
const { Title } = Typography;
const Step2_Triggers = ({ formData, setFormData, availableEvents }) => {
    const { t } = useTranslation();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedTrigger, setSelectedTrigger] = useState(null);
    const triggerEvents = formData.triggerEvents || [];
    const theme = SCENARIO_THEME.triggers;
    const IconComponent = theme.icon;
    const handleSelectionChange = (selectedNames) => {
        setFormData({ triggerEvents: selectedNames });
    };
    const handleRemoveTrigger = (triggerNameToRemove) => {
        const newTriggers = triggerEvents.filter(t => t !== triggerNameToRemove);
        handleSelectionChange(newTriggers);
    };
    const firstSelectedEvent = useMemo(() => {
        if (triggerEvents.length === 0)
            return null;
        return availableEvents.find(e => e.name === triggerEvents[0]);
    }, [triggerEvents, availableEvents]);
    const filteredEvents = useMemo(() => {
        if (!firstSelectedEvent) {
            return availableEvents;
        }
        const firstProviders = JSON.stringify(firstSelectedEvent.dataProvidersNames.slice().sort());
        return availableEvents.filter(event => {
            const currentProviders = JSON.stringify(event.dataProvidersNames.slice().sort());
            return firstProviders === currentProviders;
        });
    }, [firstSelectedEvent, availableEvents]);
    const error = useMemo(() => {
        if (triggerEvents.length === 0) {
            return {
                message: t('scenarioMustHaveAtLeastOneTriggerEvent', 'A scenario must have at least one trigger event.'),
                type: "warning"
            };
        }
        if (!firstSelectedEvent) {
            return {
                message: t('triggerEventNoLongerSupported', 'The saved trigger event "{{event}}" is no longer supported. Please select a new one.', { event: triggerEvents[0] }),
                type: "error"
            };
        }
        return null;
    }, [triggerEvents, firstSelectedEvent, t]);
    const showModal = () => {
        setSelectedTrigger(null);
        setIsModalVisible(true);
    };
    const handleCancel = () => {
        setSelectedTrigger(null);
        setIsModalVisible(false);
    };
    const handleOk = () => {
        if (selectedTrigger) {
            handleSelectionChange([...triggerEvents, selectedTrigger]);
        }
        handleCancel();
    };
    return (React.createElement("div", null,
        React.createElement(Space, { style: { marginBottom: 24 } },
            React.createElement(IconComponent, { style: { fontSize: '20px', color: theme.color } }),
            React.createElement(Title, { level: 4, style: { margin: 0, color: theme.color } }, theme.name)),
        React.createElement(Typography.Paragraph, { style: { marginBottom: 24, color: '#666' } },
            React.createElement(Trans, { i18nKey: "selectTriggerEvents" })),
        React.createElement(List, { bordered: true, dataSource: triggerEvents, renderItem: (item) => {
                const eventDef = availableEvents.find(e => e.name === item);
                const title = eventDef ? eventDef.title : item;
                return (React.createElement(List.Item, { actions: [React.createElement(Button, { type: "link", danger: true, onClick: () => handleRemoveTrigger(item) },
                            React.createElement(Trans, { i18nKey: "remove", defaults: "Remove" }))] }, title));
            } }),
        React.createElement(Button, { onClick: showModal, type: "dashed", style: { marginTop: 16 } },
            React.createElement(Trans, { i18nKey: "addTrigger", defaults: "+ Add Trigger" })),
        React.createElement(Modal, { title: React.createElement(Trans, { i18nKey: "addTriggerEvent", defaults: "Add Trigger Event" }), open: isModalVisible, onOk: handleOk, onCancel: handleCancel, destroyOnClose: true },
            React.createElement(Select, { style: { width: '100%' }, placeholder: React.createElement(Trans, { i18nKey: "selectTriggerEventPlaceholder", defaults: "Please select a trigger event" }), value: selectedTrigger, onChange: setSelectedTrigger, options: filteredEvents
                    .filter(e => !triggerEvents.includes(e.name))
                    .map(event => ({
                    label: `${event.title} (${event.name})`,
                    value: event.name,
                })) })),
        React.createElement("div", { style: { marginTop: '16px' } },
            error && React.createElement(Alert, { message: error.message, type: error.type, showIcon: true }),
            firstSelectedEvent && (React.createElement(Alert, { message: React.createElement(Trans, { i18nKey: "dataProvidedTitle", defaults: "Data Provided" }), description: t('dataProvidedDescription', 'These events will provide the following data sources: {{providers}}', { providers: firstSelectedEvent.dataProvidersNames.join(', ') }), type: "info", showIcon: true })))));
};
export default Step2_Triggers;
